/**
 * Mounts an OEM web component that is delivered from another origin.
 *
 * The element is acquired over the network, not through a static import, and it owns its own
 * shadow root — so this component's whole job is to load the module, create the tag, hand over
 * the one input that still crosses (the session), and stay out of the way.
 *
 * IT SETS NO ATTRIBUTES. The element observes none: its environment, API base URL and build
 * version are compiled into the bundle behind `loaderUrl`, one build per environment. A host
 * that configured it would be a host that could misconfigure it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@dealer-center/core-components";
import { LoadingIndicator } from "../LoadingIndicator";
import type { OemSession } from "./contract";
import { SI_SESSION_ENDED_EVENT } from "./contract";
import { loadOemElement } from "./loader";

export const OEM_ELEMENT_LOADING_TESTID = "oem-element-loading";
export const OEM_ELEMENT_UNAVAILABLE_TESTID = "oem-element-unavailable";

export interface OemElementProps {
  /** The one published URL for this environment, from `libs/core/enviroment`. */
  loaderUrl: string;
  /** The custom element the bundle registers, from `./contract`. */
  tagName: string;
  /** Omitted means the element runs anonymous. A property on the element, never an attribute. */
  session?: OemSession;
  /** Called when the element reports that the service refused its token. */
  onSessionEnded?: () => void;
}

type Acquisition = "pending" | "ready" | "failed";

interface OemHostElement extends HTMLElement {
  session?: OemSession;
}

export const OemElement = ({
  loaderUrl,
  tagName,
  session,
  onSessionEnded
}: OemElementProps) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<OemHostElement | null>(null);
  const [acquisition, setAcquisition] = useState<Acquisition>("pending");

  // Acquisition happens once per URL, so it is deliberately not in the mounting effect below —
  // that one re-runs whenever the session changes. `loadOemElement` is idempotent per URL, so a
  // remount after a failure retries rather than staying empty.
  useEffect(() => {
    let live = true;
    loadOemElement(loaderUrl, tagName).then(
      () => {
        if (live) setAcquisition("ready");
      },
      () => {
        if (live) setAcquisition("failed");
      }
    );
    return () => {
      live = false;
    };
  }, [loaderUrl, tagName]);

  // Memoised on the parts rather than the object: the element re-renders on every property
  // write, so a fresh identity each render would re-render it for nothing.
  const token = session?.token;
  const user = session?.user;
  const hostSession = useMemo<OemSession | undefined>(
    () =>
      token === undefined || user === undefined ? undefined : { token, user },
    [token, user]
  );

  useEffect(() => {
    const slot = slotRef.current;
    if (slot === null) return;
    // Before the module resolves the tag is unregistered, and `createElement` returns an
    // HTMLUnknownElement that silently accepts the property below and upgrades into nothing.
    if (acquisition !== "ready") return;

    const element: OemHostElement =
      elementRef.current ?? (document.createElement(tagName) as OemHostElement);

    element.session = hostSession;

    const handleSessionEnded = () => onSessionEnded?.();
    element.addEventListener(SI_SESSION_ENDED_EVENT, handleSessionEnded);

    // Configure first, append second: `connectedCallback` runs on append, so appending first
    // would mount the element before it has been told anything.
    if (elementRef.current === null) {
      elementRef.current = element;
      slot.append(element);
    }

    return () => {
      element.removeEventListener(SI_SESSION_ENDED_EVENT, handleSessionEnded);
    };
  }, [acquisition, tagName, hostSession, onSessionEnded]);

  // Detaching runs the element's own `disconnectedCallback`, which unmounts its React root and
  // cancels its in-flight requests. Kept in its own []-dependency effect so a re-render never
  // tears the instance down — that would take its cache and the focused element with it.
  useEffect(
    () => () => {
      elementRef.current?.remove();
      elementRef.current = null;
    },
    []
  );

  if (acquisition === "failed") {
    return (
      <Alert
        data-testid={OEM_ELEMENT_UNAVAILABLE_TESTID}
        role="alert"
        type="error"
      >
        These screens could not be loaded. Reloading usually resolves it; if it
        does not, the release is still rolling out.
      </Alert>
    );
  }

  return (
    <Box>
      {acquisition === "pending" && (
        <LoadingIndicator
          actionName="Loading OEM Compliance Center"
          data-testid={OEM_ELEMENT_LOADING_TESTID}
          size="small"
          text="Loading the OEM compliance screens"
        />
      )}
      {/* A leaf: the element owns its shadow root, so React must never render children into it.
          `Box` forwards its ref to the root DOM node, so the repo's Interstate convention holds
          even for an imperatively populated slot. */}
      <Box ref={slotRef} />
    </Box>
  );
};
